import React, {useState} from 'react';
import algoliasearch from 'algoliasearch/lite';
import {Configure, connectHits, InstantSearch} from 'react-instantsearch-dom';
import {compact} from 'lodash';
import cx from 'classnames';

import {
  ClearFilter,
  CurrentFilterDescriptor,
  Pagination,
  RangeSlider,
  RefinementList,
  RegionFilter,
  SortSelector
} from '../algolia-widgets';
import {FilterBarLayout} from '../algolia-widgets/FilterBarLayout';
import {PackageCard} from './PackageCard';
import {useMetaDescriptionCorrespondingToFields} from '../algolia-widgets/useMetaDescriptionCorrespondingToFields';
import {
  createURL,
  useSynchronizedQueryStringForSearchForPackages,
} from '../algolia-widgets/useSynchronizedQueryStringForSearchForPackages';
import {EmptyState} from "../EmptyState/EmptyState";

const searchClient = algoliasearch(
  process.env.MIX_ALGOLIA_APP_ID,
  process.env.MIX_ALGOLIA_PUBLIC_KEY
);

const WORDING_PER_TIER = {
  Iron: {
    heading:
      'Start anew with an Iron rank League of Legends account, giving you the opportunity to explore different strategies and champions. These accounts serve as a solid foundation for players looking to redefine their gameplay experience and climb the ranks. Embark on a renewed adventure in the world of LoL with an Iron rank account!',
    text:
      'League of Legends remains a global sensation, attracting millions of dedicated players worldwide who strive for greatness. However, achieving success demands immense practice and patience, while others simply seek the fun of the game. What could be more enjoyable than utterly defeating your opponents or practicing among the bottom 1% of League of Legends players?\n\n' +
      'Reaching Iron rank involves rapid de-ranking, which requires 30-35 consecutive losses after reaching level 30. Achieving this without incurring a ban is a challenging task, as intentionally losing games can cause other players to report you. Save time and money by avoiding these risks and choosing from our vast selection of ranked accounts in every region.\n\n' +
      "Whether you're looking for an Iron account to dominate your opponents, a Gold account to join your friends in duo matches, or a Diamond+ account to showcase your skills in higher tiers, there's no better place to buy a ranked LoL account online. Don't forget to use the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're the most reliable source for your account needs.",
    unrankedText:
      'We offer a wide range of ranked smurf accounts across multiple servers, catering to those who want to play with friends at a specific tier, experience the game at a higher or lower level, or practice a new role and champion on a separate account. Our commitment to providing hand-leveled accounts, top-notch customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales sets us apart from other LoL account smurf sellers.\n\n' +
      "For those who have experienced the frustration of a banned League of Legends account, we understand the pain of losing time and money invested. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! So, choose us to buy Iron LoL accounts and enjoy a worry-free gaming experience.",
  },
  Bronze: {
    heading:
      'Step up your game with a Bronze rank League of Legends account, allowing you to hone your skills and rise through the ranks. Ideal for those focused on improving their gameplay and mastering new champions, a Bronze rank account provides a suitable environment to test your abilities and learn from the competition. Embrace the challenge and progress with a Bronze rank account!',
    text:
      'As a globally renowned game, League of Legends attracts millions of passionate players striving to prove their skills and climb the ranks. While reaching the top demands dedication and practice, many players find immense satisfaction in the competitive yet accessible environment of Bronze rank. This level of play offers the ideal setting for players to refine their strategies, learn from their experiences, and steadily progress in the ranks.\n\n' +
      'With a Bronze LoL account, you can enjoy a challenging yet rewarding gameplay experience that caters to both casual players and those looking to improve their skills. Connect with like-minded players and build your reputation as a formidable competitor in the Bronze rank.',
    unrankedText:
      'At Unranked Smurfs, we pride ourselves on offering a diverse range of ranked smurf accounts across multiple servers, perfect for those looking to experience the game at various levels, join friends in specific tiers, or practice new roles and champions on a separate account. Our commitment to providing hand-leveled accounts, exceptional customer service, and competitive prices, along with an outstanding lifetime warranty on all sales, distinguishes us from other LoL account smurf sellers.\n\n' +
      "We understand the frustration of losing a League of Legends account due to a ban, resulting in wasted time and money. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. In the event your ranked account is banned for account sharing, we'll promptly provide you with a new ranked equivalent. Choose us for your Bronze League of Legends account needs and enjoy a worry-free gaming experience.",
  },
  Silver: {
    heading:
      "Challenge yourself with a Silver rank League of Legends account, where you'll have the chance to prove your skills and climb the ladder. Silver rank accounts offer a balanced environment for players looking to develop their gameplay strategies and learn from both victories and defeats. Embrace the competition and grow as a player with a Silver rank account!",
    text:
      'League of Legends continues to captivate players worldwide, with millions fighting to achieve greatness in this globally popular game. Climbing the ranks requires dedication, practice, and patience, but some players want a more balanced and competitive environment to further develop their skills. A Silver League of Legends account is the perfect choice for those seeking a challenge while having fun and learning from both victories and defeats.\n\n' +
      "With a Silver rank account, you can dive into a more intense gaming experience, refine your strategies, and improve your gameplay. Our extensive selection of ranked accounts in every region caters to every player's needs and preferences.\n\n" +
      "Whether you desire a Silver account to test your abilities in a balanced setting, a Gold account to join your friends in duo matches, or a Diamond+ account to showcase your skills in higher tiers, there's no better place to buy a ranked LoL account online. Remember to use the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're the most reliable source for your account needs.",
    unrankedText:
      'We offer a wide range of ranked smurf accounts across multiple servers, perfect for players who want to play with friends at a specific tier, experience the game at a higher or lower level, or practice a new role and champion on a separate account. Our dedication to providing hand-leveled accounts, exceptional customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales distinguishes us from other LoL account smurf sellers.\n\n' +
      "For those who have experienced the frustration of a banned League of Legends account, we understand the anguish of losing time and money invested. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! Choose us for your Silver LoL account needs and enjoy a seamless gaming experience.",
  },
  Gold: {
    heading:
      "Dive into the thrill of more advanced battles with a Gold rank League of Legends account. As you face tougher opponents, you'll have the opportunity to fine-tune your strategies and improve your gameplay. Gold rank accounts serve as an excellent platform for dedicated players striving to reach even greater heights. Challenge yourself and progress through the ranks with a Gold rank account!",
    text:
      'League of Legends continues to captivate millions of passionate players around the globe, all aiming to reach the top. While some players relish the challenge of climbing the ranks, others prefer to enjoy the thrill of competing in the Gold tier. Gold rank offers a perfect balance of excitement and competition, attracting players who seek a fun and rewarding gaming experience.\n\n' +
      'To join the Gold tier, you need a Gold League of Legends account, which provides you with the perfect opportunity to team up with friends, learn from a diverse pool of players, and continue refining your skills. By choosing one of our Gold LoL accounts, you can dive straight into the Gold-tier action without the grind of leveling up.',
    unrankedText:
      'We take pride in offering a comprehensive selection of ranked smurf accounts across multiple servers, tailored to players with varied goals and skill levels.\n\n' +
      "Whether you're aiming to play with friends in the Gold tier, learn from different players, or challenge yourself with new champions and roles, our Gold League of Legends accounts are the perfect choice. " +
      'Our commitment to providing hand-leveled accounts, exceptional customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales sets us apart from other LoL account smurf sellers.\n\n' +
      "Losing a League of Legends account to a ban can be devastating, which is why we ensure that all our accounts are hand-leveled, and we offer a free lifetime warranty on all purchases. In the unlikely event that your Gold ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! Buy your Gold LoL account here and relish a seamless gaming experience.",
  },
  Platinum: {
    heading:
      "Immerse yourself in the high-stakes world of Platinum rank League of Legends play. With a Platinum rank account, you'll face formidable opponents and continue refining your skills, strategies, and champion mastery. This level of play offers a thrilling challenge for experienced players eager to push their limits and achieve new heights. Prove your worth and thrive in the competitive atmosphere of Platinum rank!",
    text:
      'As a globally renowned game, League of Legends captivates millions of players striving to showcase their skills and climb the ranks. The Platinum rank offers a competitive and rewarding experience for dedicated players who want to prove their mettle and engage in more strategic gameplay. Challenging yourself in Platinum rank matches will enable you to refine your abilities and climb the ladder to even higher tiers.\n\n' +
      "Whether you're looking for a Platinum account to compete at a higher level, a Gold account to play with friends and duo more rapidly, or a Diamond+ account to test your skills in the upper echelons of the game, there's no better place to buy a ranked LoL account online. Don't forget to use the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're the most reliable source for your account needs.",
    unrankedText:
      'We offer a wide range of ranked smurf accounts across multiple servers, catering to those who want to play with friends at a specific tier, experience the game at a higher or lower level, or practice a new role and champion on a separate account. Our commitment to providing hand-leveled accounts, top-notch customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales sets us apart from other LoL account smurf sellers.\n\n' +
      "For those who have experienced the frustration of a banned League of Legends account, we understand the pain of losing time and money invested. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! So, choose us for your Platinum LoL account needs and enjoy a worry-free gaming experience.",
  },
  Diamond: {
    heading:
      'Experience the pinnacle of competition with a Diamond rank League of Legends account. As one of the top tiers in the game, Diamond rank presents a unique opportunity for dedicated players to test their abilities against the very best. Dive headfirst into the intense battles and continue to evolve your gameplay as you strive for supremacy. Embrace the challenge and claim your place among the elite with a Diamond rank account!',
    text:
      'As one of the most renowned games globally, League of Legends has captivated millions of players seeking to ascend the ranks and achieve the best. Climbing to the prestigious Diamond rank requires dedication, skill, and determination, making it an exhilarating challenge for those who crave the thrill of high-level competition.\n\n' +
      'Reaching Diamond rank offers you the chance to test your abilities against formidable opponents, refine your gameplay, and showcase your mastery of various champions. Save time and effort by choosing from our vast selection of ranked accounts in every region.\n' +
      "Whether you're searching for a Diamond account to prove your prowess in the elite tiers, a Gold account to play with friends, or an Iron account to experience the game at a different level, there's no better place to buy a ranked LoL account online. Don't forget to use the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're the most reliable source for your account needs.",
    unrankedText:
      'We offer a wide range of ranked smurf accounts across multiple servers, catering to those who want to play with friends at a specific tier, experience the game at a higher or lower level, or practice a new role and champion on a separate account. Our commitment to providing hand-leveled accounts, top-notch customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales sets us apart from other LoL account smurf sellers.\n\n' +
      "For those who have experienced the frustration of a banned League of Legends account, we understand the pain of losing time and money invested. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! So, choose us as your Diamond LoL account seller and enjoy a worry-free gaming experience.",
  },
  Master: {
    heading:
      "Ascend to greatness with a Master rank League of Legends account, where only the most skilled players dare to compete. Master rank offers the ultimate challenge, pitting you against the finest competitors in the game. With a Master rank account, you'll have the chance to prove your strategic prowess and secure your position as one of the top players in LoL. Rise to the occasion and etch your name in the annals of League of Legends history!",
    text:
      'League of Legends remains a worldwide gaming phenomenon, drawing millions of ambitious players who aim to reach the pinnacle of competitive play. For those who wish to take on the ultimate challenge and prove their skills among the very best, a Master rank League of Legends account provides the perfect opportunity. ' +
      'Ascending to Master rank demands exceptional dedication and skill, but it also grants you access to a competitive environment where only the most elite players compete. Becoming a part of this exclusive group allows you to sharpen your abilities and test your strategies against top-tier opponents.\n\n' +
      "Whether you're seeking a Master account to dominate the highest levels of play or simply aspire to experience the game in the most challenging environment, there's no better place to buy a ranked LoL account online. Don't forget to use the special coupon code at the top of the page to secure a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're your go-to source for all your gaming needs.",
    unrankedText:
      'We offer an extensive range of ranked smurf accounts across multiple servers, designed for players who want to join their friends at a specific tier, experience the game at the highest level, or practice a new role and champion on a separate account. Our dedication to providing hand-leveled accounts, top-notch customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales distinguishes us from other LoL account smurf sellers.\n\n' +
      "For those who have experienced the disappointment of a banned League of Legends account, we understand the heartache of losing the time and money invested. That's why all of our accounts are hand-leveled by us, and we offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! Choose UnrankedSmurfs to buy your Master League of Legends account and enjoy a seamless gaming experience.",
  },
  Grandmaster: {
    heading:
      "Reach new heights of gameplay with a GrandMaster rank League of Legends account. Join the elite few who have mastered the game's intense competition and strategic challenges. Prove your worth among the best players and dominate the competitive arena with a GrandMaster rank account!",
    text:
      "League of Legends continues to captivate millions of players worldwide, offering a platform for competitive gaming and skillful battles. The GrandMaster rank represents the epitome of achievement, where only a select few can claim their spot. If you're a player who craves the ultimate challenge and wants to test your abilities against top-tier opponents, a GrandMaster League of Legends account is your gateway to the highest level of competition.\n\n" +
      "Reaching the GrandMaster rank requires exceptional skill, in-depth knowledge of the game, and the ability to adapt to various strategies. With a GrandMaster account, you'll have the opportunity to refine your gameplay, learn from the best, and establish your dominance in the League of Legends community.\n\n" +
      "Whether you desire a GrandMaster account to showcase your prowess among the elite, a Diamond account to experience high-level play, or a Bronze account to start anew and improve your skills, our comprehensive selection of ranked accounts in every region has you covered. Take advantage of the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're the most reliable source for all your account needs.",
    unrankedText:
      "At Unranked Smurfs, we're dedicated to providing a wide range of ranked smurf accounts across multiple servers, catering to players with different goals and aspirations. Whether you want to experience the game at the highest level, play with friends in specific tiers, or explore new roles and champions on a separate account, we have the perfect GrandMaster League of Legends account for you.\n\n" +
      'Our commitment to hand-leveling all our accounts, delivering exceptional customer service, and maintaining competitive prices, combined with an outstanding lifetime warranty on all sales, sets us apart from other LoL account smurf sellers.\n\n' +
      "We understand the frustration of losing a League of Legends account due to a ban, which is why we ensure that all our accounts are hand-leveled and offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll promptly provide you with a new ranked equivalent. Choose us for your GrandMaster League of Legends account needs and enjoy a worry-free gaming experience at the highest level of competition.",
  },
  Challenger: {
    heading:
      "Achieve the extraordinary with a Challenger rank League of Legends account, reserved for the most elite players. The Challenger rank represents the zenith of competition, offering the chance to compete against the very best and showcase your unparalleled skills. With a Challenger rank account, you'll push the boundaries of your abilities and cement your legacy as a true League of Legends champion. Embrace the ultimate challenge and conquer the apex of competitive play!",
    text:
      'League of Legends stands as a global phenomenon, drawing millions of passionate players from around the world, all vying for dominance. Reaching the pinnacle of success demands incredible dedication, practice, and skill. For those seeking the ultimate competitive experience, Challenger rank offers the chance to test your abilities against the very best in the game.\n\n' +
      "Securing a Challenger rank account allows you to push your limits and showcase your unparalleled skills in the upper echelon of League of Legends play. Join the most elite players and prove your worth in the highest tiers of competition. There's no better place to buy a ranked LoL account online than our vast selection of accounts across all regions.\n" +
      "Whether you're searching for a Challenger account to experience the thrill of playing among the elite or want to dive into other ranked tiers, we have the perfect account for you. Use the special coupon code at the top of the page to enjoy a generous discount on your ranked smurfs account. With our competitive prices and high-quality accounts, we're your go-to source for all your League of Legends needs.",
    unrankedText:
      'We offer an extensive range of ranked smurf accounts on multiple servers, perfect for those who want to experience the game at the highest level or explore other tiers and roles on a separate account. Our commitment to providing hand-leveled accounts, exceptional customer service, and maintaining competitive prices with an outstanding lifetime warranty on all sales sets us apart from other LoL account smurf sellers.\n\n' +
      "We understand the frustration of having a League of Legends account banned, resulting in the loss of time and money invested. That's why we hand-level all of our accounts and offer a free lifetime warranty on all purchases. If your ranked account is banned for account sharing, we'll provide you with a new ranked equivalent as soon as possible! Choose us for your Challenger LoL account needs and enjoy a worry-free gaming experience at the apex of competition.",
  },
};

export function PackagesPage({ ranked = false, regions } = {}) {
  const { searchState, onSearchStateChange } =
    useSynchronizedQueryStringForSearchForPackages({
      automaticallyManagedRefinementLists: ['champions'],
      automaticallyManagedRanges: compact([
        'price',
        'min_skins',
        'min_champions',
        !ranked && 'min_blue_essence',
      ]),
    });

  const { refinementList } = searchState;
  useMetaDescriptionCorrespondingToFields(
    refinementList.region_name,
    refinementList['tier'] ?? refinementList['rank.tier']
  );

  const [accountName] = useState(() =>
    new URL(window.location.href).href.split('/')
  );

  const { tier, region_name } = searchState.refinementList;

  const exactlyOneTier = tier && tier.length === 1;
  function getAccountNamesWithRegionsOrRanks() {
    const exactlyOneRegion = region_name && region_name.length === 1;
    // Both
    if (exactlyOneTier && exactlyOneRegion) {
      return region_name[0] + ' ' + tier[0];
    }

    // Only tier
    if (exactlyOneTier) {
      return tier[0];
    }

    // Only region
    if (exactlyOneRegion) {
      return region_name[0];
    }

    // Neither
    return accountName[6].split('?')[0];
  }

  function getHeadingTextForRankedOrUnrankedPage() {
    return tier ? WORDING_PER_TIER[tier[0]]?.heading : null;
  }

  function getLeagueOfLegendsAccountText() {
    return tier ? WORDING_PER_TIER[tier[0]]?.text : null;
  }

  function getBuyFromUnrankedSmurfsText() {
    return tier ? WORDING_PER_TIER[tier[0]]?.unrankedText : null;
  }

  const moreThanOneTier = !tier || tier.length > 1;
  const isRanked = accountName[6].split('?')[0] === 'ranked';
  const isUnranked = accountName[6].split('?')[0] === 'unranked';
  const exactlyOneRegion = region_name && region_name.length === 1;


  function getTier() {
    if (ranked) {
      if(exactlyOneTier) {
        return `${tier} `;
      }
    }

    return ''
  }

  function getRank() {
    if(ranked) {
      return 'Ranked ';
    }

    return 'Unranked ';
  }

  function getRegion() {
    if(exactlyOneRegion) {
      return `${region_name} `;
    }

    return ''
  }

  function generateSchemaName() {
    return `Browse ${getRank()}${getRegion()}${getTier()}LoL Accounts`
  }

  function generationSchemaDescription() {
    return `Skip the leveling! Buy a ${getRank().toLowerCase()}${getRegion()}LoL account. All accounts listed here are level 30+ and immediately eligible for the ranked queue.`
  }

  function generateSchemaURL() {
    const baseURL = "https://www.unrankedsmurfs.com/league-of-legends/lol-account/category/"
    let finalURL = baseURL + getRank().trim().toLowerCase() + '/'

    if(exactlyOneRegion) {
      finalURL = finalURL + getRegion().trim() + '/'
    }

    if(exactlyOneTier && isRanked) {
      finalURL = finalURL + getTier().trim() + '/'
    }

    return finalURL
  }

  function generateSchemaBreadcrumb() {
    const baseBreadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "item": {
            "@id": "https://www.unrankedsmurfs.com/",
            "name": "UnrankedSmurfs Homepage"
          }
        },
        {
          "@type": "ListItem",
          "position": 2,
          "item": {
            "@id": "https://www.unrankedsmurfs.com/league-of-legends",
            "name": "League Of Legends"
          }
        },
        {
          "@type": "ListItem",
          "position": 3,
          "item": {
            "@id": `https://www.unrankedsmurfs.com/league-of-legends/lol-account/category/${ranked ? 'ranked' : 'unranked'}`,
            "name": `Buy ${ranked ? 'Ranked' : 'Unranked'} League Of Legends Accounts`
          }
        }
      ]
    }

    if(exactlyOneTier && isRanked) {
      baseBreadcrumb.itemListElement.push({
        "@type": "ListItem",
        "position": 5,
        "item": {
          "@id": `https://www.unrankedsmurfs.com/league-of-legends/lol-account/category/${ranked ? 'ranked' : 'unranked'}${'/'+getTier().trim()}`,
          "name": `${getTier().trim()} LoL Accounts`
        }
      })
      return baseBreadcrumb
    }

    if(exactlyOneRegion) {
     baseBreadcrumb.itemListElement.push({
       "@type": "ListItem",
       "position": 4,
       "item": {
         "@id": `https://www.unrankedsmurfs.com/league-of-legends/lol-account/category/${ranked ? 'ranked' : 'unranked'}${'/'+getRegion().trim()}`,
         "name": `${getRegion().trim()} LoL Accounts`
       }
     })
      return baseBreadcrumb
    }

    return baseBreadcrumb
  }

  // Dynamically update meta based on region and URLs
  document.querySelector('#schema-collection-page').innerHTML = JSON.stringify(
    {
      "@context": "http://schema.org",
      "@type": "CollectionPage",
      "name": generateSchemaName(),
      "description": generationSchemaDescription(),
      "url": generateSchemaURL(),
    }, null, 3)

  document.querySelector('#schema-breadcrumb').innerHTML = JSON.stringify(generateSchemaBreadcrumb(), null, 3)

  return (
    <>
      <div>
        <div className="packages-section-group-img">
          <div className="packages-section-arrow-girl">
            <div className="p-2 lg:w-5/6 lg:mx-auto">
              <div className="flex mt-10 p-2 lg:p-0 heading-3 lg:heading-2">
                <p className="hidden lg:inline heading-gold lg:mr-2">|</p>
                {`Browse ${getAccountNamesWithRegionsOrRanks()} Accounts`}
              </div>
              <div className="lg:flex items-center">
                <div className="p-2 lg:w-3/5 mt-2 lg:mt-0">
                  {moreThanOneTier && isRanked
                    ? 'League of Legends ranked accounts. We offer accounts ranked in the previous season with inflated MMR or ' +
                      'accounts with all placement games and rank unlocked in this season. Want to know more about buying process?'
                    : isUnranked
                    ? 'League of Legends unranked accounts. All accounts listed here are level 30+ and immediately eligible for ' +
                      'the ranked queue. Skip the leveling! Want to know more about buying process? Click here'
                    : getHeadingTextForRankedOrUnrankedPage()}
                </div>
                <div className="flex-1" />
                <div className="mt-4 lg:flex lg:items-center lg:justify-content-center text-center lg:mt-10">
                  <a
                    className={cx(
                      'border-2 border-honestly-white text-sm p-2',
                      isUnranked
                        ? 'bg-honestly-white text-almost-black'
                        : 'text-honestly-white'
                    )}
                    href="/league-of-legends/lol-account/category/unranked"
                  >
                    Unranked Accounts
                  </a>
                  <a
                    className={cx(
                      'border-2 border-honestly-white ml-6 text-sm p-2',
                      isRanked
                        ? 'bg-honestly-white text-almost-black'
                        : 'text-honestly-white'
                    )}
                    href="/league-of-legends/lol-account/category/ranked"
                  >
                    Ranked Accounts
                  </a>
                </div>
              </div>
              <React.Fragment>
                <InstantSearch
                  indexName="packages_by_id_asc"
                  searchClient={searchClient}
                  searchState={searchState}
                  onSearchStateChange={onSearchStateChange}
                  createURL={createURL}
                >
                  <Configure
                    filters={`ranked:${ranked}`}
                    hitsPerPage={40}
                  />
                  <FilterBarLayout
                    regionWidget={
                      <RegionFilter
                        attribute="region_name"
                        defaultRegions={['NA', 'EUW', 'EUNE', 'BR', 'OCE', 'LAN', 'JP', 'LAS', 'TR', 'RU']}
                      />
                    }
                    widgets={
                      <React.Fragment>
                        {ranked && (
                          <RefinementList
                            attribute="tier"
                            name="Rank"
                            limit={100}
                            searchable
                          />
                        )}
                        <RangeSlider attribute="price" name="Price" unit="$" />
                        <RangeSlider
                          attribute="min_champions"
                          name="Champions"
                          side={ranked ? 'left' : 'right'}
                        />
                        {!ranked && (
                          <RangeSlider
                            attribute="min_blue_essence"
                            name="Blue Essence"
                            side="left"
                          />
                        )}
                      </React.Fragment>
                    }
                    bottomLeftWidgets={<CurrentFilterDescriptor />}
                    bottomRightWidgets={
                      <React.Fragment>
                        <ClearFilter />
                        <SortSelector
                          defaultRefinement="packages_by_region_id_and_price_asc"
                          items={[
                            {
                              value: 'packages_by_price_asc',
                              label: 'Price|asc',
                            },
                            {
                              value: 'packages_by_price_desc',
                              label: 'Price|desc',
                            },
                            {
                              value: 'packages_by_champions_count_asc',
                              label: 'Champions|asc',
                            },
                            {
                              value: 'packages_by_champions_count_desc',
                              label: 'Champions|desc',
                            },
                          ]}
                        />
                      </React.Fragment>
                    }
                  />

                  <PackagesList />

                  <Pagination />
                </InstantSearch>
              </React.Fragment>
            </div>
          </div>
        </div>
        <div className="packages-section-professor-fiora">
          <div>
            <div className="packages-section-lifetime">
              <div className="lg:flex hidden lg:flex-wrap lg:justify-center text-center lg:p-16">
                <div className="flex">
                  <img
                    src="/images/packages/timer.png"
                    className="clock-img"
                    alt="timer logo"
                  />
                  <p className="text-base w-4/5 ml-2">
                    {' '}
                    24/7 Instant delivery to your email address{' '}
                  </p>
                </div>
                <div className="flex">
                  <img
                    src="/images/packages/lock.png"
                    className="clock-img"
                    alt="lock logo"
                  />
                  <p className="text-base ml-2"> Free Lifetime Warranty </p>
                </div>
                <div className="flex ml-6">
                  <img
                    src="/images/packages/diamond.png"
                    className="diamond-img"
                    alt="lock logo"
                  />
                  <p className="text-base ml-2"> Hand Leveled Account </p>
                </div>
                <div className="flex ml-6">
                  <img
                    src="/images/packages/star.png"
                    className="star-img"
                    alt="lock logo"
                  />
                  <p className="text-base ml-2"> #1 Rated Accounts store </p>
                </div>
              </div>

              <div className="lg:hidden mt-4">
                <div className="flex flex-wrap justify-center text-center">
                  <img
                    src="/images/packages/timer_mobi.png"
                    className="clock-img mt-4 mr-2"
                    alt="timer logo"
                  />
                  <p className="text-md ml-3 display-linebreak">
                    {' '}
                    {'24 / 7 Instant delivery \nto your email address'}{' '}
                  </p>
                </div>
                <div className="flex flex-wrap justify-center text-center mt-6">
                  <img
                    src="/images/packages/lock_mobi.png"
                    className="clock-img ml-2"
                    alt="lock logo"
                  />
                  <p className="text-md ml-4"> Free Lifetime Warranty </p>
                </div>
                <div className="flex flex-wrap justify-center text-center mt-6">
                  <img
                    src="/images/packages/diamond_mobi.png"
                    className="diamond-img"
                    alt="lock logo"
                  />
                  <p className="text-md ml-3"> Hand Leveled Account </p>
                </div>
                <div className="flex flex-wrap justify-center text-center ml-4 mt-6">
                  <img
                    src="/images/packages/star_mobi.png"
                    className="star-img"
                    alt="lock logo"
                  />
                  <p className="text-md ml-3"> #1 Rated Accounts store </p>
                </div>
              </div>

              <div>
                <div className="lg:flex lg:flex-wrap lg:justify-center text-center p-2 mt-10 text-h3">
                  {`Lifetime Warranty On All ${
                    region_name && isUnranked ? region_name[0] : ''
                  } Accounts`}
                </div>
                <div className="text-center lg:w-4/5 lg:mx-auto mt-10 lg:mt-0 p-2 lg:p-6">
                  Our League of Legends accounts come with a lifetime warranty
                  giving you that extra peace of mind. We’re so confident in the
                  quality of our accounts that we are the only League of Legends
                  account seller that offers and 100% honours our lifetime
                  warranty on all accounts you buy. If your smurf account gets
                  banned or has any inherent flaw, we’ll issue you with a new
                  equivalent account. New LoL accounts will be issued according
                  to our{' '}
                  <a href="/terms-and-conditions" className="text-smurfs-gold">
                    Terms of Service agreement.
                  </a>
                </div>
                <div className="lg:flex lg:flex-wrap lg:justify-center text-center lg:p-16 mt-4 lg:mt-0">
                  <p>Instant 24/7 Delivery</p>

                  <div className="hidden lg:flex lg:flex-wrap lg:justify-center lg:ml-16 lg:mt-4 lg:text-center">
                    <div className="border-t w-8 text-smurfs-gold" />
                  </div>

                  <p className="lg:ml-16 mt-4 lg:mt-0">Lifetime Warranty</p>
                  <div className="hidden lg:flex lg:flex-wrap lg:justify-center lg:ml-16 lg:mt-4 lg:text-center">
                    <div className="border-t w-8 text-smurfs-gold" />
                  </div>
                  <p className="lg:ml-16 mt-4 lg:mt-0">
                    Secure Payment Options
                  </p>
                </div>
                <div className="lg:flex lg:flex-wrap lg:justify-center text-center">
                  <p className="mt-4 lg:mt-0">Human Leveled Accounts</p>
                  <div className="hidden lg:flex lg:flex-wrap lg:justify-center lg:ml-16 lg:mt-4 lg:text-center">
                    <div className="border-t w-8 text-smurfs-gold" />
                  </div>
                  <p className="lg:ml-16 mt-4 lg:mt-0">Over 5000 5* Reviews</p>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center mt-10 lg:mt-20 p-2 heading-3">
            {`Why Get A ${getAccountNamesWithRegionsOrRanks()} League Of Legends Account?`}
          </div>
          <div className="lg:hidden flex flex-wrap justify-center">
            <div className="border-t w-8 text-smurfs-gold" />
          </div>
          <div className="display-linebreak lg:w-4/5 lg:mx-auto p-2 text-center mt-2 lg:mt-0 lg:p-16">
            {moreThanOneTier && isRanked
              ? 'League of Legends continues to be one of the most popular games in the world. With millions of  monthly active players from all over the world ' +
                'battling to become the  best. However, becoming the best requires an immense amount of practice  and patience – especially if you’re stuck in ' +
                'Gold or lower.\n\nClimbing the ranks can be  difficult when your teammates don’t care about the game – that’s where  one of our ranked accounts can save you. Once ' +
                'you’ve been granted the  ability to play at higher tiers, you’ll find that everything runs much  smoother compared to the likes of elo hell. Don’t ' +
                'get us wrong, we’re  sure you can climb out of the lower elo brackets yourself, but why not  save yourself all the stress and time? Our accounts ' +
                'are all ranked  ready, with a large roster of Champions for you to enjoy.\n\n' +
                'Here at UnrankedSmurfs, we  have a massive selection of ranked accounts in every region. No matter  whether you’re looking for a Gold account ' +
                'to play with your friends, or  to show off to them – there is no better place to buy a ranked LoL  account online.\n\n' +
                'Remember to use the special  coupon code found at the top of the page to get a generous discount on  your ranked smurf account. ' +
                'Combined with our very competitive prices,  and high-quality accounts, we’re the most reliable place to pick up your  account.'
              : isUnranked
              ? 'League of Legends is currently one of the most popular games globally. With millions of monthly active users, players worldwide battle it out' +
                ' to become the best. \n\nTo become the best, you need a safe space to practice new champions, roles and strategies without endangering your current' +
                ' rank and putting yourself behind. \n\n At Unranked Smurfs, we have the most extensive collection of unranked LoL accounts in every region.\n\n' +
                "if you're looking for a fresh smurf account or a premium skins account, there is no better place than Unranked Smurfs. Offering competitive " +
                'prices, Unranked Smurfs remains the most reliable platform or smurfs accounts.'
              : getLeagueOfLegendsAccountText()}
          </div>
        </div>
        <div className="how-it-works mt-6 lg:mt-0">
          <div className="lg:flex lg:flex-wrap lg:justify-center text-center text-h4 p-4">
            How It Works:
          </div>
          <div className="hidden lg:flex flex-wrap justify-center text-center">
            <div className="border-t w-8 text-smurfs-gold" />
          </div>
          <div className="lg:flex lg:flex-wrap lg:justify-center text-center lg:p-10">
            <div className="mt-4 lg:mt-0">
              <img src="/images/packages/cart-outline.png" alt="cart logo" className="inline"/>
              <div className="mt-2">Add To Cart</div>
              <div className="text-xs p-4 display-linebreak">
                {'Choose the account\n you like the most and\n add to cart'}
              </div>
            </div>

            <div className="lg:hidden">
              <i className="lg:ml-10 fas fa-solid fa-arrow-down" />
            </div>

            <div className="lg:inline hidden">
              <i className=" fas fa-solid fa-arrow-right" />
            </div>

            <div className="mt-4 lg:mt-0 lg:ml-10 ">
              <img src="/images/packages/money.png" alt="cash icon" className="inline" />
              <div className="mt-2">Checkout</div>
              <div className="text-xs p-4 display-linebreak">
                {'Pay with one of the\n 200 payment methods\n available'}
              </div>
            </div>

            <div className="lg:hidden">
              <i className="lg:ml-10 fas fa-solid fa-arrow-down" />
            </div>

            <div className="lg:inline hidden">
              <i className="lg:ml-10 fas fa-solid fa-arrow-right" />
            </div>

            <div className="mt-4 lg:mt-0 lg:ml-10 ">
              <img src="/images/packages/delivery-truck.png" alt="delivery truck icon" className="inline" />
              <div className="mt-2">Delivery</div>
              <div className="text-xs p-4 display-linebreak">
                {
                  'Within 1 min receive\n an account and\n instructions how to\n verify and care for it'
                }
              </div>
            </div>

            <div className="lg:hidden">
              <i className="lg:ml-10 fas fa-solid fa-arrow-down" />
            </div>

            <div className="lg:inline hidden">
              <i className="lg:ml-10 fas fa-solid fa-arrow-right" />
            </div>

            <div className="mt-4 lg:mt-0 lg:ml-10 ">
              <img src="/images/packages/support.png" alt="headset icon" className="inline"/>
              <div className="mt-2">24/7 Support</div>
              <div className="text-xs p-4 display-linebreak">
                {
                  ' customer support\n with over 10 years of\n experience in helping\n our customers'
                }
              </div>
            </div>
          </div>
        </div>
        <div className="packages-section-ranked-1">
          <div className="text-center lg:w-3/4 lg:mx-auto mt-20 heading-3">
            {`Why Buy ${getAccountNamesWithRegionsOrRanks()} League Of Legends Accounts From Unranked Smurfs?`}
          </div>
          <div className="lg:hidden mt-2 flex flex-wrap justify-center">
            <div className="border-t w-8 text-smurfs-gold" />
          </div>
          <div className="display-linebreak text-md lg:w-2/3 lg:mx-auto p-2 text-center mt-2 lg:mt-0 lg:p-4">
            {isUnranked
              ? "At Unranked Smurfs, we offer a range of unranked level 30 smurf accounts and premium skin accounts. Whether you're looking for a second account " +
                'to practice on or add some super rare skins to your collection, we have all the LoL accounts you need.\n\nUnlike other LoL account smurf sellers, ' +
                "we go out of our way to ensure we offer the best accounts, customer service and range of products. We're the one-stop-shop for everything you need," +
                " with thousands of accounts in stock.\n\nIf you've ever had your accounts banned in the past, then you'll know how frustrating it can be. You lose your " +
                "money, but you also lose all your progress. Luckily, here at Unranked Smurfs, we're different. Not only do we hand level all of our accounts to ensure " +
                "they don't get banned, but we also offer a free lifetime warranty on all purchases. If your account gets banned in the unlikely event, you'll receive a " +
                'new one entirely for free.\n\nHere are more reasons why you should get your League of Legends accounts from Unranked Smurfs:'
              : getBuyFromUnrankedSmurfsText()}
          </div>
        </div>
      </div>
    </>
  );
}

const PackagesList = connectHits(({ hits }) => {
  if (hits.length === 0) {
    return (
      <div className="valorant-category-page__search-container">
        <EmptyState />
      </div>
    )}

  const products = hits.map(({__position, url, title}) => ({
    "@type": "ListItem",
    "position": __position,
    "url": url,
    "name": title,
    "description": `Take a look at this ${title} Unranked LoL account that is ready to jump into ranked!`,
  }))

  document.querySelector('#schema-itemlist').innerHTML = JSON.stringify(
    {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": products
    }, null, 3)

  return (
    <div className="flex flex-wrap mt-8 -ml-4">
      {hits.map((pkg, index) => (
        <PackageCard key={pkg.id} pkg={pkg} index={index} />
      ))}
    </div>
  );
});
